/* eslint-disable jsx-a11y/anchor-is-valid */
import { default as classNames } from 'classnames'
import React, { useEffect, useState } from 'react'
import { Tab } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import dayjs from 'dayjs'
import FilterListIcon from '@mui/icons-material/FilterList'

import {
  QUIZZALLREVIEWREQUESTSBYID,
  QUIZZDETAILSBYID,
  EXERCISES_ARCHIVE,
  GET_QUIZ_BYID,
} from '@/config'
import { deleteQuiz, getAllQuizzes, restoreQuiz } from '@/store/quizzes/actions'
import { getCommonState } from '@/store/common/selector'
import { Quiz } from '@/models/quiz.model'
import PermissionWrapper from '@/components/UI/PermissionWrapper/PermissionWrapper'
import CustomMenu from '@/components/UI/CustomMenu'
import BasicModal from '@/components/UI/Dialog'
import QuizQuestionBank from './QuizQuestionBank/QuizQuestionBank'
import CreateQuizModalTeachersToolkit from '../TeachersToolkit/popups/createQuizModalTeachersToolkit'
import emptyKnowledge from '@/assets/New-images/empty-knowledge.svg'
import KGroupsTree from '@/components/UI/KGroupsTree'
import CustomTabs from '@/components/UI/CustomTabs'

interface QuizzesGridViewProps {
  isArchive?: boolean
  createFromQuestionBank?: any
  quizId?: any
  addMoreQuestionToQuiz?: any
  createQuizModal: 'quiz' | 'roleplay' | undefined | null
  setCreateQuizModal: React.Dispatch<
    React.SetStateAction<'quiz' | 'roleplay' | undefined | null>
  >
}

const QuizzesGridView: React.FC<QuizzesGridViewProps> = ({
  isArchive = false,
  createFromQuestionBank,
  quizId,
  addMoreQuestionToQuiz,
  createQuizModal,
  setCreateQuizModal,
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const commonState = useSelector(getCommonState)
  const [isLoading, setLoading] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  // const [isRolePlayEnabled, setRolePlayEnabled] = useState(false)
  const [filters, setFilters] = useState({
    sort_field: 'updated_at',
    sort_order: 'dsc',
    topic_ids: null,
    knowledge_group_ids: null,
    q: null,
    includes_all_subgroups: null,
    isArchive: isArchive,
  })
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false)
  const [deletedRowId, setDeletedRowId] = useState('')
  const [quizzes, setQuizzes] = useState<Quiz[]>([])
  const [selectedQuestions, setSelectedQuestions] = useState([])

  // Mobile responsive state
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [isTreeDrawerOpen, setIsTreeDrawerOpen] = useState(false)

  const defaultTab = 'quizzes'
  const validTabs = ['quizzes', 'roleplay', 'bank']
  const [activeTab, setActiveTab] = useState<string>(defaultTab)

  const handleClickViewActivity = (quiz: Quiz) => {
    const quizType = activeTab === 'roleplay' ? 'roleplay' : 'quiz'
    history.push(
      `${QUIZZDETAILSBYID.replace(':quizId', quiz._id).replace(
        ':quizType',
        quizType,
      )}`,
    )
  }

  const handleClickEdit = (quiz: Quiz) => {
    if (isArchive) {
      restoreQuizId(quiz?._id)
    } else {
      history.push(GET_QUIZ_BYID.replace(':quizId', quiz._id))
    }
  }

  const handleClickDelete = (quiz: Quiz) => {
    if (isArchive) {
      setDeletedRowId(quiz._id)
      setShowConfirmDelete(true)
    } else {
      deleteQuizzHandler(quiz._id)
    }
  }

  const ACTION_BUTTONS = [
    {
      label: t('View Activity'),
      onClickAction: handleClickViewActivity,
      icon: <DatasetOutlinedIcon />,
    },
    {
      label: isArchive ? t('Restore') : t('Edit'),
      onClickAction: handleClickEdit,
      icon: <EditOutlinedIcon />,
    },
    {
      label: t('Delete'),
      onClickAction: handleClickDelete,
      icon: <DeleteOutlinedIcon />,
    },
  ]

  const deleteQuizzHandler = (id: string) => {
    setDeletedRowId(id)
    setIsShowDeleteModal(true)
  }

  useEffect(() => {
    if (selectedGroup) {
      getAllQuizzesFun({
        ...filters,
        knowledge_group_ids: selectedGroup._id,
        includes_all_subgroups: true,
      })
    } else if (commonState.selectedGroupPath.length) {
      getAllQuizzesFun({
        ...filters,
        knowledge_group_ids:
          commonState.selectedGroupPath[
            commonState.selectedGroupPath.length - 1
          ],
        includes_all_subgroups: true,
      })
    }
  }, [filters, selectedGroup, activeTab])

  const getAllQuizzesFun = async (filters: any) => {
    try {
      setLoading(true)
      const response: any = await dispatch(
        getAllQuizzes({
          ...filters,
          type: activeTab === 'roleplay' ? 'roleplay' : 'default',
        }),
      )
      setLoading(false)
      setQuizzes(response)
    } catch (error) {
      setLoading(false)
      console.log('error', error)
    }
  }

  /**
   * Set debounce time for while searching in any input
   */
  const [query, setQuery] = useState('')
  const [isTyping, setTyping] = useState(false)
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isTyping) {
        setFilters({
          ...filters,
          q: query,
        })
        setTyping(false)
      }
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [query])

  const onInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setQuery(e.target.value)
    setTyping(true)
  }

  /**
   * Used to delete multiple knowledge based on IDS
   * @param e
   * @param selectedRowIds
   * @param isArchive
   */
  const deleteConfirmed = async (
    selectedRowIds: string,
    isArchive: boolean = false,
  ) => {
    setShowConfirmDelete(false)
    await dispatch(deleteQuiz(selectedRowIds, isArchive))
    getAllQuizzesFun({
      ...filters,
      knowledge_group_ids: selectedGroup?._id,
      includes_all_subgroups: true,
      // includes_all_subgroups: hasTopLevelApplied,
    })
  }

  const restoreQuizId = async (quizId: string) => {
    await dispatch(restoreQuiz(quizId))
    getAllQuizzesFun({
      ...filters,
      knowledge_group_ids: selectedGroup?._id,
      includes_all_subgroups: true,
      // includes_all_subgroups: hasTopLevelApplied,
    })
  }

  const onSortHandler = (selector: any, direction: string) => {
    setFilters({
      ...filters,
      sort_field: selector,
      sort_order: direction,
    })
  }

  const CHIP_LABELS = {
    single_question: t('Single Question Format'),
    continuous_scroll: t('All-at-once Format'),
    conversational_v2: t('Conversational Quiz'),
    conversational: t('Conversational Legacy'),
  }

  const quizHtml = (
    <>
      <div className="all-knowledge-search tw-flex tw-items-center tw-flex-wrap tw-gap-2 tw-mb-2">
        <CustomMenu
          textTrigger={t('Sort')}
          options={[
            {
              label: t('Most Recent'),
              onClick: () => onSortHandler('updated_at', 'dsc'),
            },
            {
              label: t('Ascending'),
              onClick: () => onSortHandler('name', 'asc'),
            },
            {
              label: t('Descending'),
              onClick: () => onSortHandler('name', 'dsc'),
            },
          ]}
          slotProps={{
            trigger: {
              startIcon: <FilterListIcon fontSize="small" />,
              size: 'small',
              variant: 'outlined',
              sx: { padding: '8px 12px', fontWeight: 700 },
            },
          }}
        />

        <div className="web-links my-0 mr-0">
          <input
            type="text"
            className="form-control h-auto"
            placeholder={
              activeTab === 'roleplay'
                ? t('Search for role play')
                : t('Search for quiz')
            }
            onChange={onInputChangeHandler}
          />
        </div>

        {!isArchive && (
          <PermissionWrapper>
            <Button
              sx={{ ml: 'auto' }}
              onClick={() =>
                setCreateQuizModal(
                  activeTab === 'roleplay' ? 'roleplay' : 'quiz',
                )
              }
            >
              + {t('Add')}
            </Button>
          </PermissionWrapper>
        )}
      </div>
      <div className="tw-mt-4 tw-grid tw-gap-4 xl:tw-gap-6 tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 2xl:tw-grid-cols-4">
        {quizzes.map(quiz => {
          return (
            <Stack
              gap={2}
              onClick={() => {
                if (isLoading) return
                !isArchive &&
                  history.push(GET_QUIZ_BYID.replace(':quizId', quiz._id))
              }}
              key={quiz._id}
              justifyContent="space-between"
              className={classNames(
                'tw-relative tw-border tw-rounded-lg tw-px-4 xl:tw-px-6 tw-pt-6 tw-pb-4 tw-cursor-pointer tw-shadow-md hover:tw-shadow-xl',
                isLoading ? 'tw-animate-pulse tw-duration-75' : '',
              )}
            >
              {quiz.reviews_count > 0 && (
                <Typography
                  variant="caption2"
                  className=" tw-absolute tw-top-0 tw-right-0 tw-bg-preview tw-text-white tw-rounded-lg tw-rounded-br-none tw-px-3 tw-py-2"
                  onClick={e => {
                    e?.stopPropagation()
                    history.push(
                      QUIZZALLREVIEWREQUESTSBYID.replace(':quizId', quiz._id),
                    )
                  }}
                  sx={{ lineHeight: 1 }}
                >
                  {quiz.reviews_count} {t('pending review(s)')}
                </Typography>
              )}

              <Stack gap={1}>
                <Tooltip title={quiz.name} placement="top-start">
                  <Typography
                    variant="subtitle2"
                    component="h5"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: '3',
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {quiz.name}
                  </Typography>
                </Tooltip>
                {quiz.breadcrumbs && quiz.breadcrumbs.length > 0 && (
                  <Typography
                    variant="caption2"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: '#5F5E5E',
                    }}
                  >
                    {quiz.breadcrumbs.map((b: any) => b.name).join('/')}
                  </Typography>
                )}
                <Stack direction="row" gap="4px" alignItems="center">
                  <Typography variant="caption2" color="textGrey">
                    {dayjs(quiz.created_at).format('DD/MM/YYYY hh:mm A')}
                  </Typography>
                  {quiz.type === 'default' && (
                    <>
                      <div className="tw-w-1 tw-h-1 tw-rounded-full tw-bg-grey" />
                      <Typography
                        variant="caption2"
                        color="textGrey"
                        component="span"
                        sx={{ lineHeight: 1 }}
                      >
                        {quiz?.provisional_question_count || 0}{' '}
                        {quiz?.provisional_question_count > 1
                          ? t('Questions')
                          : t('Question')}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>

              <Stack gap={2}>
                {quiz.type === 'default' && (
                  <Chip
                    label={
                      CHIP_LABELS[quiz.display_mode as keyof typeof CHIP_LABELS]
                    }
                    className="tw-self-start"
                    sx={{ color: '#6B7085', bg: '#F8F7F8' }}
                  />
                )}

                <Stack
                  direction="row"
                  sx={{
                    display: { xl: 'flex', xs: 'none' },
                    borderTop: '1px solid #E5E7EB',
                  }}
                  pt={2}
                >
                  {ACTION_BUTTONS.map((action, index) => (
                    <Button
                      key={index}
                      onClick={e => {
                        e.stopPropagation()
                        action.onClickAction(quiz)
                      }}
                      size="small"
                      color={index === 2 ? 'error' : 'primary'}
                      sx={{ fontSize: { xs: '12px', lg: '14px' } }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  gap="4px"
                  sx={{
                    display: { xl: 'none', xs: 'flex' },
                    borderTop: '1px solid #E5E7EB',
                  }}
                  pt={2}
                >
                  {ACTION_BUTTONS.map((action, index) => (
                    <Tooltip title={action.label} key={index}>
                      <IconButton
                        onClick={e => {
                          e.stopPropagation()
                          action.onClickAction(quiz)
                        }}
                        color={index === 2 ? 'error' : 'primary'}
                      >
                        {action.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          )
        })}
      </div>

      {quizzes && quizzes.length === 0 && (
        <div className="empty-knowledge text-center my-5">
          <h3 className="h6 clr-grey-txt7 mb-3">
            {t('You have yet to create quiz')}
          </h3>
          <figure className="h-auto">
            <img src={emptyKnowledge} alt="" />
          </figure>
          <Button
            onClick={() =>
              setCreateQuizModal(activeTab === 'roleplay' ? 'roleplay' : 'quiz')
            }
            sx={{ mt: 2 }}
          >
            {t('Click here to get started')}
          </Button>
        </div>
      )}
    </>
  )

  return (
    <>
      <BasicModal
        open={isShowDeleteModal}
        onClose={() => setIsShowDeleteModal(false)}
        title={showConfirmDelete ? t('Confirm Delete') : t('Delete Quiz')}
        showCloseButton
        sx={{ p: 2, height: 'auto', width: '500px' }}
      >
        <Typography variant="body3" component="p" sx={{ my: 2 }}>
          {showConfirmDelete ? (
            <>
              {t(
                'Are you sure you would like to delete? Deleted items would be',
              )}
              <Typography variant="body3" component="span" color="error">
                {t(' deleted permanently')}
              </Typography>
            </>
          ) : (
            t('Would you like to archive or delete the quiz?')
          )}
        </Typography>
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              if (showConfirmDelete) {
                setShowConfirmDelete(false)
                setIsShowDeleteModal(false)
                deleteConfirmed(deletedRowId)
              } else {
                setShowConfirmDelete(true)
              }
            }}
          >
            {t('Delete')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (showConfirmDelete) {
                setShowConfirmDelete(false)
                setIsShowDeleteModal(false)
              } else {
                setIsShowDeleteModal(false)
                deleteConfirmed(deletedRowId, true)
              }
            }}
          >
            {showConfirmDelete ? t('Cancel') : t('Archive')}
          </Button>
        </Stack>
      </BasicModal>

      <CreateQuizModalTeachersToolkit
        show={!!createQuizModal}
        selectedGroupId={selectedGroup?._id}
        onHide={() => setCreateQuizModal(null)}
        // isRolePlayEnabled={isRolePlayEnabled}
        isRolePlayEnabled={createQuizModal === 'roleplay'}
      />

      <div>
        {createFromQuestionBank && (
          <div className="levels-row mb-4 d-flex">
            <div className="ml-auto">
              {' '}
              <button
                className="btn btn-primary font-400 font-16 p-1 px-3"
                onClick={() => {
                  addMoreQuestionToQuiz(selectedQuestions, quizId)
                }}
                disabled={selectedQuestions?.length === 0}
              >
                {t('Import to Quiz')}
              </button>
            </div>
          </div>
        )}
        <div className="tw-flex tw-flex-col md:tw-flex-row">
          {/* Mobile Drawer */}
          {isMobile && (
            <Drawer
              variant="temporary"
              open={isTreeDrawerOpen}
              onClose={() => setIsTreeDrawerOpen(false)}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: { xs: '75vw', sm: '280px' },
                  maxWidth: '320px',
                  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                  border: 'none',
                },
              }}
            >
              <KGroupsTree
                actions={{ add: true, edit: true, delete: true }}
                archivedRoute={EXERCISES_ARCHIVE}
                showsManageGroups={true}
                onSelectGroup={group => {
                  setSelectedGroup(group)
                  setIsTreeDrawerOpen(false)
                }}
              />
            </Drawer>
          )}

          {/* Desktop Inline */}
          {!isMobile && (
            <KGroupsTree
              actions={{ add: true, edit: true, delete: true }}
              archivedRoute={EXERCISES_ARCHIVE}
              showsManageGroups={true}
              slotProps={{ root: { className: 'tw-w-full md:tw-w-2/12' } }}
              onSelectGroup={group => {
                setSelectedGroup(group)
              }}
            />
          )}

          <div className="tw-w-full md:tw-w-10/12">
            {/* Mobile Groups Toggle Button */}
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setIsTreeDrawerOpen(true)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                mb: 2,
                ml: 2,
              }}
            >
              {t('Groups')}
            </Button>

            {!createFromQuestionBank && (
              <Box
                className="tw-px-4 tw-py-2"
                sx={{ '& nav': { display: 'inline-flex' } }}
              >
                <CustomTabs
                  defaultActiveKey={defaultTab}
                  activeKey={activeTab}
                  validTabs={validTabs}
                  queryParamName="activeTab"
                  onTabChange={key => setActiveTab(key)}
                >
                  <Tab eventKey="quizzes" title={t('Quizzes')}>
                    {activeTab === 'quizzes' && quizHtml}
                  </Tab>
                  <Tab eventKey="roleplay" title={t('Role Plays')}>
                    {activeTab === 'roleplay' && quizHtml}
                  </Tab>
                  <Tab eventKey="bank" title={t('Question Bank')}>
                    {activeTab === 'bank' && (
                      <QuizQuestionBank
                        knowledge_group_ids={selectedGroup?._id}
                        includes_all_subgroups={true}
                        selectedYear={commonState.selectedYear}
                        activeTab={activeTab}
                        isArchive={isArchive}
                        setSelectedQuestions={() => {}}
                      />
                    )}
                  </Tab>
                </CustomTabs>
              </Box>
            )}
            {createFromQuestionBank && (
              <div className="px-3 py-2 text-left">
                <QuizQuestionBank
                  knowledge_group_ids={selectedGroup?._id}
                  // includes_all_subgroups={hasTopLevelApplied}
                  includes_all_subgroups={true}
                  selectedYear={commonState.selectedYear}
                  activeTab={activeTab}
                  isArchive={isArchive}
                  createFromQuestionBank={createFromQuestionBank}
                  setSelectedQuestions={setSelectedQuestions}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default QuizzesGridView
